import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../context/SidebarContext';
import { supabase } from '../lib/supabase';
import {
  loadOnboardingSeen,
  isSeen as isOnboardingSeen,
  markSeen as markOnboardingSeen,
} from '../lib/onboardingSeen';
import { getProjectThumbnailSignedUrl } from '../utils/projectThumbnail';
import { Plus, Trash2, Film, Loader2, MoreHorizontal, Monitor, ChevronDown } from 'lucide-react';
import { WelcomeEditorModal } from '../components/WelcomeEditorModal';

interface VideoProject {
  id: string;
  name: string;
  duration: number;
  thumbnail_file_name: string | null;
  created_at: string;
  updated_at: string;
  brand_profile_id: string | null;
}

const formatRelativeDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  if (diffHours < 24) return `Hace ${diffHours}h`;
  if (diffDays === 1) return 'Ayer';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short' });
};

const formatDuration = (seconds: number): string => {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const VideoProjects: React.FC = () => {
  const navigate = useNavigate();
  const { sidebarWidth } = useSidebar();

  const [projects, setProjects] = useState<VideoProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  // Bienvenida del editor (primera vez). El "ya la vi" se persiste en la DB
  // (profiles.onboarding_seen → llave "welcome"), cross-device, con fallback a
  // localStorage.
  const [showWelcome, setShowWelcome] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Signed URL por proyecto (clave: project.id). El thumbnail vive en un bucket
  // privado, así que la columna `thumbnail_file_name` guarda el storage path.
  const [thumbUrls, setThumbUrls] = useState<Record<string, string>>({});
  // Brand filter dropdown (same style as the Historial filter). `null` selection
  // means "Todas las marcas". Defaults to all on every visit (not persisted) and
  // is only shown when the company has 2+ brands — with one brand there's
  // nothing to filter.
  const [brands, setBrands] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedBrandId, setSelectedBrandId] = useState<string | null>(null);
  const [brandDropdownOpen, setBrandDropdownOpen] = useState(false);

  const fetchProjects = useCallback(async (cId: string) => {
    const { data, error } = await supabase
      .from('video_projects')
      .select('id, name, duration, thumbnail_file_name, created_at, updated_at, brand_profile_id')
      .eq('id_company', cId)
      .order('updated_at', { ascending: false });

    if (!error && data) {
      setProjects(data as VideoProject[]);
      const withThumb = (data as VideoProject[]).filter(p => p.thumbnail_file_name);
      Promise.all(
        withThumb.map(async p => {
          const url = await getProjectThumbnailSignedUrl(p.thumbnail_file_name as string);
          return [p.id, url] as const;
        }),
      ).then(pairs => {
        const next: Record<string, string> = {};
        for (const [id, url] of pairs) if (url) next[id] = url;
        setThumbUrls(next);
      });
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;
        setUserId(user.id);
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        const cId = (profile as any)?.id_company as string | undefined;
        if (!cId || cancelled) return;
        setCompanyId(cId);
        // Load the company's brands so the filter dropdown can list them. With a
        // single brand the dropdown stays hidden (nothing to filter).
        const { data: brandRows } = await supabase
          .from('brand_profiles')
          .select('id, name')
          .eq('id_company', cId)
          .eq('is_draft', false)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: true });
        if (!cancelled && brandRows) {
          setBrands(
            brandRows.map((b: any) => ({ id: b.id, name: b.name || 'Marca' })),
          );
        }
        await fetchProjects(cId);
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchProjects]);

  // Mostrar la bienvenida la primera vez (una vez que tenemos companyId, así
  // "Crear proyecto" puede crear con la marca actual). Esperamos a que cargue el
  // estado de la DB (cross-device) antes de decidir, para NO mostrarla de nuevo
  // en otra PC si el usuario ya la vio.
  useEffect(() => {
    if (!companyId || !userId) return;
    let cancelled = false;
    (async () => {
      await loadOnboardingSeen(userId);
      if (cancelled) return;
      if (!isOnboardingSeen('welcome')) setShowWelcome(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, userId]);

  const markWelcomeSeen = () => {
    markOnboardingSeen('welcome', userId);
  };

  const handleCreate = async () => {
    if (!companyId || creating) return;
    setCreating(true);
    try {
      // Pick the brand to attach to the new project. Resolution order:
      // the brand selected in the list filter → the user's current_brand_id →
      // company default → create one.
      let brandProfileId: string | null = null;
      // 0) If the list is filtered to a specific brand, create the project under
      // that brand so it matches what the user is currently viewing (and shows
      // up in the filtered list instead of disappearing).
      if (selectedBrandId) brandProfileId = selectedBrandId;
      // 1) Otherwise prefer the user's active brand so a new project opens on
      // the brand they're actually working with. Only honored when it still
      // belongs to this company and isn't a draft.
      if (!brandProfileId) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: prof } = await supabase
            .from('profiles')
            .select('current_brand_id')
            .eq('id', user.id)
            .maybeSingle();
          const activeId = (prof as any)?.current_brand_id as string | null;
          if (activeId) {
            const { data: activeBrand } = await supabase
              .from('brand_profiles')
              .select('id')
              .eq('id', activeId)
              .eq('id_company', companyId)
              .eq('is_draft', false)
              .maybeSingle();
            if (activeBrand?.id) brandProfileId = activeBrand.id;
          }
        }
      }
      // 2) Fallback: the company default brand (or the oldest non-draft one).
      if (!brandProfileId) {
        const { data: existing } = await supabase
          .from('brand_profiles')
          .select('id')
          .eq('id_company', companyId)
          .eq('is_draft', false)
          .order('is_default', { ascending: false })
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        if (existing?.id) brandProfileId = existing.id;
      }
      // 3) Still none? Create a default brand for the company. Use the company
      // name so the "Memoria" header reads "Memoria de <Company>" out of the box.
      if (!brandProfileId) {
        const { data: companyRow } = await supabase
          .from('company_profiles')
          .select('nombre')
          .eq('id_company', companyId)
          .maybeSingle();
        const brandName = (companyRow?.nombre || '').trim() || 'Marca principal';
        const { data: created, error: createErr } = await supabase
          .from('brand_profiles')
          .insert({
            id_company: companyId,
            name: brandName,
            context: '',
            colors: [],
            is_default: true,
          })
          .select('id')
          .single();
        if (createErr) console.error('brand_profiles insert failed', createErr);
        brandProfileId = created?.id || null;
      }

      const { data, error } = await supabase
        .from('video_projects')
        .insert({
          id_company: companyId,
          name: 'Proyecto sin título',
          state: {},
          duration: 30,
          brand_profile_id: brandProfileId,
        })
        .select('id')
        .single();
      if (!error && data) {
        navigate(`/editor-video/${data.id}`);
      }
    } catch {
      // ignore
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await supabase.from('video_projects').delete().eq('id', id);
      setProjects(prev => prev.filter(p => p.id !== id));
    } catch {
      // ignore
    } finally {
      setDeletingId(null);
      setMenuOpenId(null);
    }
  };

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpenId) return;
    const handler = () => setMenuOpenId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [menuOpenId]);

  // Close brand filter dropdown on outside click
  useEffect(() => {
    if (!brandDropdownOpen) return;
    const handler = () => setBrandDropdownOpen(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [brandDropdownOpen]);

  // `null` selection = all brands; otherwise narrow to the chosen brand.
  const selectedBrand = selectedBrandId
    ? brands.find(b => b.id === selectedBrandId) ?? null
    : null;
  const visibleProjects = selectedBrandId
    ? projects.filter(p => p.brand_profile_id === selectedBrandId)
    : projects;
  const showBrandFilter = !loading && projects.length > 0 && brands.length >= 2;

  return (
    <>
      {/* Mobile: not available */}
      <div className="md:hidden flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
        <div className="w-16 h-16 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
          <Monitor className="w-7 h-7 text-neutral-400" />
        </div>
        <h2 className="text-[15px] font-semibold text-brand-black/90 mb-2">Solo disponible en desktop</h2>
        <p className="text-[13px] text-neutral-500 max-w-sm">
          El estudio de video necesita una pantalla más grande. Abrilo desde una computadora.
        </p>
      </div>

      {/* Desktop */}
      <div className="hidden md:block fixed inset-0 z-20 bg-surface-bg overflow-y-auto" style={{ left: sidebarWidth }}>
        {/* Bienvenida del editor (primera vez) → "Crear proyecto" crea con la
            marca actual y entra al editor (donde arranca el tour guiado). */}
        <WelcomeEditorModal
          open={showWelcome}
          creating={creating}
          onCreate={() => {
            markWelcomeSeen();
            void handleCreate();
          }}
          onSkip={() => {
            markWelcomeSeen();
            setShowWelcome(false);
          }}
        />
        <div className="max-w-4xl mx-auto px-6 py-10">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-[17px] font-semibold text-brand-black/90">Proyectos de video</h1>
              <p className="text-[13px] text-[#3C3C43]/60 mt-1">Creá y editá videos con IA</p>
            </div>
            <div className="flex items-center gap-2">
              {showBrandFilter && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setBrandDropdownOpen(o => !o);
                    }}
                    className={`py-2 px-4 inline-flex items-center justify-between gap-2 border rounded-md text-[13px] font-medium shadow-sm outline-none transition-colors cursor-pointer whitespace-nowrap bg-surface-card hover:bg-surface-elevated text-neutral-900 border-neutral-200`}
                  >
                    <span className="max-w-[160px] truncate">
                      {selectedBrand?.name ?? 'Todas las marcas'}
                    </span>
                    <ChevronDown
                      className={`w-4 h-4 text-neutral-400 transition-transform ${brandDropdownOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {brandDropdownOpen && (
                    <div className="absolute z-50 mt-1 right-0 min-w-[180px] bg-surface-card border border-neutral-200 rounded-md shadow-md py-1 animate-fade-in">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedBrandId(null);
                          setBrandDropdownOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-[13px] transition-colors ${
                          selectedBrandId === null
                            ? 'bg-neutral-100 text-neutral-900 font-medium'
                            : 'text-neutral-600 hover:bg-neutral-100'
                        }`}
                      >
                        Todas las marcas
                      </button>
                      {brands.map(b => (
                        <button
                          key={b.id}
                          type="button"
                          onClick={() => {
                            setSelectedBrandId(b.id);
                            setBrandDropdownOpen(false);
                          }}
                          className={`w-full text-left px-3 py-2 text-[13px] truncate transition-colors ${
                            selectedBrandId === b.id
                              ? 'bg-neutral-100 text-neutral-900 font-medium'
                              : 'text-neutral-600 hover:bg-neutral-100'
                          }`}
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {!loading && projects.length > 0 && (
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="py-2 px-4 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
                >
                  {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                  Nuevo proyecto
                </button>
              )}
            </div>
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-brand-primary" />
            </div>
          )}

          {/* Empty state */}
          {!loading && projects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-[15px] font-semibold text-brand-black/90 mb-1">Sin proyectos</h3>
              <p className="text-[13px] text-[#3C3C43]/60 max-w-xs mb-5">
                Creá tu primer proyecto de video para empezar a editar con IA.
              </p>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Crear proyecto
              </button>
            </div>
          )}

          {/* Filtered-empty: projects exist, but none for the active brand */}
          {!loading && projects.length > 0 && visibleProjects.length === 0 && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-14 h-14 rounded-lg bg-neutral-100 flex items-center justify-center mb-4">
                <Film className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-[15px] font-semibold text-brand-black/90 mb-1">
                Sin proyectos en {selectedBrand?.name}
              </h3>
              <p className="text-[13px] text-[#3C3C43]/60 max-w-xs mb-5">
                No tenés proyectos de video en esta marca todavía. Creá uno nuevo o cambiá el filtro a "Todas las marcas".
              </p>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="py-2.5 px-5 rounded-md text-[13px] font-medium shadow-sm transition-colors flex items-center gap-1.5 bg-brand-primary hover:bg-brand-primary-hover text-white disabled:opacity-60"
              >
                {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                Crear proyecto
              </button>
            </div>
          )}

          {/* Project grid */}
          {!loading && visibleProjects.length > 0 && (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Existing projects */}
              {visibleProjects.map(project => (
                <div
                  key={project.id}
                  className="group relative rounded-lg bg-surface-card border border-black/[0.06] hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/editor-video/${project.id}`)}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video bg-brand-black/[0.03] flex items-center justify-center relative rounded-t-lg overflow-hidden">
                    {project.thumbnail_file_name && thumbUrls[project.id] ? (
                      <img
                        src={thumbUrls[project.id]}
                        alt={project.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Film className="w-8 h-8 text-neutral-300" />
                    )}
                    {/* Duration badge */}
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-[11px] font-medium px-1.5 py-0.5 rounded">
                      {formatDuration(project.duration)}
                    </div>
                  </div>

                  {/* Info */}
                  <div className="px-3 py-2.5 flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-medium text-brand-black/90 truncate">{project.name}</p>
                      <p className="text-[11px] text-[#3C3C43]/60 mt-0.5">
                        Editado {formatRelativeDate(project.updated_at)}
                      </p>
                    </div>

                    {/* Menu button */}
                    <div className="relative">
                      <button
                        onClick={e => { e.stopPropagation(); setMenuOpenId(menuOpenId === project.id ? null : project.id); }}
                        className="w-7 h-7 flex items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors opacity-0 group-hover:opacity-100"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>

                      {/* Dropdown */}
                      {menuOpenId === project.id && (
                        <div
                          className="absolute right-0 top-8 z-50 w-40 bg-surface-card rounded-md shadow-lg border border-black/[0.06] py-1 animate-in fade-in slide-in-from-top-1 duration-150"
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleDelete(project.id)}
                            disabled={deletingId === project.id}
                            className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 transition-colors disabled:opacity-60"
                          >
                            {deletingId === project.id ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Trash2 className="w-3.5 h-3.5" />
                            )}
                            Eliminar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default VideoProjects;
